import { Injectable } from '@angular/core';

import { MachineApiService } from './machine-api.service';

type ApiResponse<T> = { data: T; success: boolean };

export const MACHINE_FEEDBACK_PROJECT_ID = '37be2f95-b838-495b-80a0-5ae3bebc68d3';

export type FeedbackSubmissionInput = {
  title: string;
  description: string;
  route?: string;
  pageTitle?: string;
};

export type FeedbackAgentifyStatus = {
  attempted: boolean;
  source: 'auto' | 'manual';
  status: 'completed' | 'failed';
  movedToBacklog: boolean;
  attemptedAt: number;
  completedAt?: number;
  error?: string;
};

export type FeedbackTicketCard = {
  id: string;
  ticketNumber?: number;
  title: string;
  description?: string;
  repoRole?: 'site' | 'api' | 'agent-api' | 'infra' | 'unknown';
  acceptanceCriteria?: string[];
  risk?: 'low' | 'med' | 'high';
  feedback?: Array<{
    source?: string;
    kind?: string;
    summary: string;
    at?: number;
    route?: string;
    pageTitle?: string;
    submittedByUserId?: string;
  }>;
  agentify?: {
    status?: 'completed' | 'failed';
    source?: 'auto' | 'manual';
    lastAttemptAt?: number;
    completedAt?: number;
    error?: string;
  };
};

export type FeedbackSubmissionResult = {
  projectId: string;
  column: {
    id: string;
    title: string;
    kind: 'backlog' | 'execute' | 'review' | 'done' | 'custom';
  };
  card: FeedbackTicketCard;
  boardVersion: number;
  agentify?: FeedbackAgentifyStatus;
};

export type FeedbackTicketAgentifyInput = {
  projectId: string;
  ticketId: string;
  ticketNumber?: number;
  title?: string;
  source?: 'auto' | 'manual';
  model?: string;
  projectContext?: {
    definition?: any;
    schema?: any;
    diagram?: any;
    repos?: any;
  };
};

export type FeedbackTicketAgentifyResult = {
  projectId: string;
  column: FeedbackSubmissionResult['column'];
  card: FeedbackTicketCard;
  boardVersion: number;
  agentify: FeedbackAgentifyStatus;
};

@Injectable({ providedIn: 'root' })
export class FeedbackService {
  constructor(private readonly machineApi: MachineApiService) {}

  private async unwrap<T>(promise: Promise<ApiResponse<T>>): Promise<T> {
    const res = await promise;
    return res.data;
  }

  async submitFeedback(input: FeedbackSubmissionInput): Promise<FeedbackSubmissionResult> {
    const title = String(input?.title ?? '').trim();
    const description = String(input?.description ?? '').trim();
    if (!title) throw new Error('Title is required');
    if (!description) throw new Error('Description is required');

    return this.unwrap(
      this.machineApi.postJson<ApiResponse<FeedbackSubmissionResult>>('/api/tickets/feedback', {
        projectId: MACHINE_FEEDBACK_PROJECT_ID,
        title,
        description,
        autoAgentify: true,
        source: {
          route: String(input?.route ?? '').trim() || undefined,
          pageTitle: String(input?.pageTitle ?? '').trim() || undefined,
        },
      })
    );
  }

  async agentifyTicket(input: FeedbackTicketAgentifyInput): Promise<FeedbackTicketAgentifyResult> {
    const projectId = String(input?.projectId ?? '').trim();
    const ticketId = String(input?.ticketId ?? '').trim();
    if (!projectId) throw new Error('Project id is required');
    if (!ticketId) throw new Error('Ticket id is required');
    return this.unwrap(
      this.machineApi.postJson<ApiResponse<FeedbackTicketAgentifyResult>>('/api/tickets/agentify', {
        projectId,
        ticketId,
        ticketNumber:
          typeof input?.ticketNumber === 'number' && Number.isFinite(input.ticketNumber) && input.ticketNumber > 0
            ? Math.floor(input.ticketNumber)
            : undefined,
        title: String(input?.title ?? '').trim() || undefined,
        source: input?.source === 'auto' ? 'auto' : 'manual',
        model: String(input?.model ?? '').trim() || undefined,
        projectContext: input?.projectContext ?? undefined,
      })
    );
  }
}
